import { useState } from "react"

import { StatusColor } from "~/constants/status"
import { DEMAND_LIFECYCLE_LABEL } from "~/components/demand-2/shared/demandLifecycle"
import { calendarDefaultMaxDate, DateFormatType, formatDate } from "~/utils/datetime"
import {
  formatLifecycleStatus,
  isInactiveStatus,
  type LifecycleStage,
  lifecycleStageProgress,
  lifecycleStatusColor,
  noLifecycleDatesSet,
  phasingOutNeverApplies,
} from "~/utils/lifecycleUtils"
import { distributeWithMinimum } from "~/utils/math"
import { Tooltip } from "../../reusable/Tooltip"
import { cn } from "~/utils/helpers"

const MIN_STAGE_PERCENT = 12
const MIN_PERIOD_STAGE_PERCENT = 4

const MARKER_INSET = 0.1

const bandClass = (color: StatusColor, isCurrent: boolean) => {
  switch (color) {
    case StatusColor.Error:
      return isCurrent
        ? "bg-badge-status-sqaure-error border-badge-red-border"
        : "bg-badge-red-bg border-badge-red-border"
    case StatusColor.Warning:
      return isCurrent
        ? "bg-badge-status-sqaure-warning border-badge-orange-border"
        : "bg-badge-orange-bg border-badge-orange-border"
    case StatusColor.Success:
      return isCurrent
        ? "bg-badge-status-sqaure-success border-badge-green-border"
        : "bg-badge-green-bg border-badge-green-border"
    default:
      return isCurrent
        ? "bg-badge-status-sqaure-neutral border-badge-neutral-border"
        : "bg-badge-neutral-bg border-badge-neutral-border"
  }
}

type Props = {
  status: LifecycleStage
  activatedFrom?: Date | string | null
  phasingOutFrom?: Date | string | null
  activatedUntil?: Date | string | null
  /** Draws only these weeks, to scale, from the current stage on: e.g. a plan's horizon. */
  period?: { from: Date; to: Date }
}

type Stage = {
  key: LifecycleStage
  name: string
  color: StatusColor
  from: number | null
  to: number | null
  label: string
  tooltip: string
}

const time = (date: Date | string | null | undefined) =>
  date == null ? null : new Date(date).getTime()

const short = (ms: number) => formatDate(new Date(ms), DateFormatType.FullDateShort)

export const LifecycleTimeline = ({
  status,
  activatedFrom,
  phasingOutFrom,
  activatedUntil,
  period,
}: Props) => {
  const [now] = useState(() => Date.now())
  const start = time(activatedFrom)
  const phasing = time(phasingOutFrom)
  const end = time(activatedUntil)

  const nothingSet = noLifecycleDatesSet({ activatedFrom, phasingOutFrom, activatedUntil })
  const notYetActive = status === "upcoming"
  const skipPhasingOut = phasingOutNeverApplies({ activatedUntil, phasingOutFrom })

  const horizon = calendarDefaultMaxDate.getTime()
  const measurable = (t: number | null) => (t != null && t <= horizon ? t : null)

  const span = (stage: { from: number | null; to: number | null }) => {
    const from = measurable(stage.from)
    const to = measurable(stage.to)
    return from != null && to != null ? { from, to } : null
  }

  const range = (from: number | null, to: number | null, fallback: string) => {
    const bounds = span({ from, to })
    return bounds ? `${short(bounds.from)} \u2192 ${short(bounds.to)}` : fallback
  }

  const stages: Stage[] = [
    ...(notYetActive && start != null
      ? [
          {
            key: "upcoming" as const,
            name: DEMAND_LIFECYCLE_LABEL.upcoming,
            color: lifecycleStatusColor("upcoming"),
            from: now,
            to: start,
            label: `until ${short(start)}`,
            tooltip: `Forecasting starts ${short(start)}. Until then the variant has no demand and no suggested orders.`,
          },
        ]
      : []),
    {
      key: null,
      name: nothingSet ? "No status" : formatLifecycleStatus(null),
      color: nothingSet ? StatusColor.Neutral : lifecycleStatusColor(null),
      from: start,
      to: phasing ?? end,
      label: start == null ? "No dates set" : range(start, phasing ?? end, short(start)),
      tooltip: nothingSet
        ? "No lifecycle dates are set, so the variant is forecast normally."
        : "The variant is forecast normally and can receive suggested orders.",
    },
    ...((phasing != null || status === "phasingOut") && !skipPhasingOut
      ? [
          {
            key: "phasingOut" as const,
            name: formatLifecycleStatus("phasingOut"),
            color: lifecycleStatusColor("phasingOut"),
            from: phasing,
            to: end,
            label: phasing == null ? "Set in ERP" : range(phasing, end, short(phasing)),
            tooltip:
              "The variant receives no new suggested orders and sells through remaining stock.",
          },
        ]
      : []),
    ...(end != null || status === "phasedOut"
      ? [
          {
            key: "phasedOut" as const,
            name: formatLifecycleStatus("phasedOut"),
            color: lifecycleStatusColor("phasedOut"),
            from: end,
            to: null,
            label: end == null ? "Set in ERP" : `after ${short(end)}`,
            tooltip: "No demand and no suggested orders. The variant stays in historical reports.",
          },
        ]
      : []),
  ]

  const isCurrent = (stage: Stage) =>
    stage.key === null ? !notYetActive && !isInactiveStatus(status) : stage.key === status

  const clipped = period ? clipToPeriod(stages, stages.findIndex(isCurrent), period) : null
  const shown = clipped?.map((w) => w.stage) ?? stages

  const widths = clipped
    ? distributeWithMinimum(
        clipped.map((w) => w.to - w.from),
        MIN_PERIOD_STAGE_PERCENT,
      )
    : distributeWithMinimum(
        shown.map((stage) => {
          const bounds = span(stage)
          return bounds ? Math.max(0, bounds.to - bounds.from) : 0
        }),
        MIN_STAGE_PERCENT,
      )

  const currentIndex = shown.findIndex(isCurrent)

  const currentStage = shown[currentIndex]
  const currentBounds = currentStage ? span(currentStage) : null
  const progress =
    lifecycleStageProgress(currentBounds?.from, currentBounds?.to, new Date(now)) ?? 0.5
  const cumulative = widths.map((_, i) => widths.slice(0, i + 1).reduce((sum, w) => sum + w, 0))
  const dividers = cumulative.slice(0, -1)

  const inset = Math.min(1 - MARKER_INSET, Math.max(MARKER_INSET, progress))
  const offsetBefore = currentIndex > 0 ? (cumulative[currentIndex - 1] ?? 0) : 0
  const nowLeft = offsetBefore + (widths[currentIndex] ?? 0) * inset

  return (
    <div className="flex flex-col gap-2.5">
      <div className={cn("relative", !period && "pt-5")}>
        <div className="relative h-3 rounded-full bg-medium flex">
          {shown.map((stage, index) => (
            <Tooltip
              key={stage.key ?? "active"}
              title={`${stage.name} \u00b7 ${stage.label}`}
              description={stage.tooltip}
              disableHoverableContent
              delayDuration={0}
            >
              <div
                className={cn(
                  bandClass(stage.color, index === currentIndex),
                  index === 0 && "rounded-l-full",
                  index === shown.length - 1 && "rounded-r-full",
                  "hover:relative hover:z-10 hover:shadow-borders-focus",
                )}
                style={{ width: `${widths[index]}%` }}
              />
            </Tooltip>
          ))}
          {dividers.map((offset) => (
            <div
              key={offset}
              className="absolute inset-y-0 w-0.5 bg-base"
              style={{ left: `${offset}%` }}
            />
          ))}
        </div>
        {currentIndex >= 0 && !period && (
          <div
            className="absolute top-0 bottom-0 flex flex-col items-center -translate-x-1/2"
            style={{ left: `${nowLeft}%` }}
          >
            <span className="label-sm font-medium text-fg-base whitespace-nowrap">Now</span>
            <div className="w-px flex-1 bg-fg-base" />
          </div>
        )}
      </div>

      {period && (
        <div className="-mt-1.5 flex justify-between text-xs text-fg-subtle">
          <span>Now</span>
          <span>{short(period.to.getTime())}</span>
        </div>
      )}

      <div className="flex flex-col gap-0.5">
        {shown.map((stage, index) => (
          <div key={stage.key ?? "active"} className="flex items-center gap-1.5 text-xs">
            <div
              className={cn(
                "w-2 h-2 rounded-[2px] border shrink-0",
                bandClass(stage.color, index === currentIndex),
              )}
            />
            <span
              className={cn(
                "flex-1 truncate",
                index === currentIndex ? "text-fg-base font-medium" : "text-fg-subtle",
              )}
            >
              {stage.name}
            </span>
            <span className="text-fg-subtle truncate">{stage.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * The stages a period shows: the current one from the period's start, each later one from its
 * own date, all cut at the period's end. Earlier stages are behind it.
 */
const clipToPeriod = (stages: Stage[], currentIndex: number, period: { from: Date; to: Date }) => {
  const periodFrom = period.from.getTime()
  const periodTo = period.to.getTime()
  const ahead = stages.slice(Math.max(0, currentIndex))
  return ahead
    .map((stage, i) => {
      const from = i === 0 ? periodFrom : Math.max(stage.from ?? periodTo, periodFrom)
      const next = ahead[i + 1]?.from
      const to = Math.min(next ?? stage.to ?? periodTo, periodTo)
      return { stage, from, to }
    })
    .filter((w) => w.to > w.from)
}
